import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Building2,
  Users,
  LayoutGrid,
  Monitor,
  Wifi,
  Calendar,
  BarChart3,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  ArrowRight,
  Clock,
  DollarSign,
  Thermometer,
  Wind,
  Droplets,
  Activity,
  MapPin,
  Settings,
  Lightbulb,
  Leaf,
  Target,
  Zap,
  Radio,
  Shield,
  Battery
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/Header';
import { TopBar } from '@/components/TopBar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/sections/ContactForm';
import { HreflangTags } from '@/components/HreflangTags';
import { useLanguage } from '@/i18n/LanguageContext';

// Custom generated images
const IMAGES = {
  hero: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/4ae617865e02d14865dc72daf77a8981c18611b827ca7da67dc481e6bd8339a1.png",
  dashboard: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/57b3b1c24ecd150879f7e3f643d9bb54b8981d8c9fc4456555de5c8e6f272a0d.png",
  heatmap: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/d870f4cb27ec1d0655d4e5b0cb9e299d094c19dc5b83b3d873cac2fc73312de4.png",
  sensors: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/a0ac84639a9ab17eb7747ab5f520ea10f045e328c94d735b0d4a09c3e9bd8c99.png"
};

const OfficeSpaceManagementPage = () => {
  const { t } = useLanguage();
  
  return (
    <>
      <Helmet>
        <title>{t('officeSpacePage.metaTitle')}</title>
        <meta name="description" content={t('officeSpacePage.metaDesc')} />
        <meta name="keywords" content="office space management, smart office, workplace analytics, office occupancy sensors, desk occupancy monitoring, meeting room analytics, smart workplace solutions, smart building IoT, workplace optimization, office utilization analytics" />
        <link rel="canonical" href="https://0gbaltics.com/office-space-management" />
        <meta property="og:title" content={t('officeSpacePage.metaTitle')} />
        <meta property="og:description" content={t('officeSpacePage.metaDesc')} />
        <meta property="og:type" content="website" />
      </Helmet>
      <HreflangTags />

      <TopBar />
      <Header />

      <main className="pt-20">
        
        {/* ========================================= */}
        {/* HERO SECTION */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="office-hero">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                  {t('officeSpacePage.heroBadge')}
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  {t('officeSpacePage.heroTitle')}
                </h1>
                <p className="text-xl text-slate-300 mb-6 leading-relaxed">
                  {t('officeSpacePage.heroSubtitle')}
                </p>
                <div className="flex flex-wrap gap-4 mb-8">
                  <Button 
                    size="lg"
                    className="bg-teal-500 hover:bg-teal-600 text-white"
                    onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                    data-testid="hero-cta"
                  >
                    {t('officeSpacePage.ctaButton')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
                
                {/* Feature badges */}
                <div className="flex flex-wrap gap-3">
                  {[
                    t('officeSpacePage.heroBadge1'),
                    t('officeSpacePage.heroBadge2'),
                    t('officeSpacePage.heroBadge3'),
                    t('officeSpacePage.heroBadge4')
                  ].map((tech, i) => (
                    <span key={i} className="bg-slate-800 border border-slate-700 text-slate-300 text-sm px-3 py-1 rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="relative">
                <img 
                  src={IMAGES.hero}
                  alt="Smart office environment with IoT sensors monitoring desk and meeting room occupancy"
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* WHAT IS OFFICE SPACE MANAGEMENT */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="what-is-osm">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('officeSpacePage.whatIsBadge') || 'Understanding the Challenge'}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('officeSpacePage.whatIsTitle') || 'What Is Office Space Management?'}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('officeSpacePage.whatIsDesc') || 'Office space management is the practice of understanding, monitoring, and optimizing how physical workplace spaces are used.'}
                </p>
                
                <h4 className="font-semibold text-slate-900 mb-4">{t('officeSpacePage.driversTitle') || 'Key drivers for smart office management:'}</h4>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: Users, text: t('officeSpacePage.driver1') || 'Hybrid work models' },
                    { icon: LayoutGrid, text: t('officeSpacePage.driver2') || 'Flexible office layouts' },
                    { icon: Monitor, text: t('officeSpacePage.driver3') || 'Shared desk policies' },
                    { icon: Calendar, text: t('officeSpacePage.driver4') || 'Meeting room optimization' },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-center gap-2 bg-slate-50 p-3 rounded-lg">
                        <Icon className="h-5 w-5 text-teal-600" />
                        <span className="text-slate-700 text-sm">{item.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="bg-teal-50 border border-teal-200 p-8 rounded-lg">
                <h4 className="text-teal-800 font-semibold mb-6 text-lg">{t('officeSpacePage.visibilityTitle') || 'IoT sensors provide visibility into:'}</h4>
                <div className="space-y-4">
                  {[
                    { title: t('officeSpacePage.visibility1') || 'Real-time occupancy', desc: t('officeSpacePage.visibility1Desc') || 'Which spaces are currently in use' },
                    { title: t('officeSpacePage.visibility2') || 'Usage patterns', desc: t('officeSpacePage.visibility2Desc') || 'Peak hours, quiet periods, daily trends' },
                    { title: t('officeSpacePage.visibility3') || 'Space efficiency', desc: t('officeSpacePage.visibility3Desc') || 'Utilization rates across zones' },
                    { title: t('officeSpacePage.visibility4') || 'Environmental conditions', desc: t('officeSpacePage.visibility4Desc') || 'Temperature, air quality, comfort' },
                  ].map((item, i) => (
                    <div key={i} className="bg-white p-4 rounded-lg border border-teal-100">
                      <h5 className="font-semibold text-slate-900 text-sm">{item.title}</h5>
                      <p className="text-slate-600 text-xs">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* THE HIDDEN COST OF EMPTY OFFICE SPACE */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="hidden-costs">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-red-400 mb-4">
                  {t('officeSpacePage.costBadge') || 'The Business Problem'}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                  {t('officeSpacePage.costTitle') || 'The Hidden Cost of Empty Office Space'}
                </h2>
                <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                  {t('officeSpacePage.costDesc') || 'Studies consistently show that many companies have 30-60% unused office capacity at any given time.'}
                </p>
                
                <h4 className="font-semibold text-white mb-4">{t('officeSpacePage.costsTitle')}</h4>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: DollarSign, text: t('officeSpacePage.cost1'), color: 'text-red-400' },
                    { icon: Thermometer, text: t('officeSpacePage.cost2'), color: 'text-orange-400' },
                    { icon: Zap, text: t('officeSpacePage.cost3'), color: 'text-amber-400' },
                    { icon: Lightbulb, text: t('officeSpacePage.cost4'), color: 'text-yellow-400' },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-center gap-2 bg-slate-800 p-3 rounded-lg">
                        <Icon className={`h-5 w-5 ${item.color}`} />
                        <span className="text-slate-300 text-sm">{item.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  { stat: '30-60%', label: t('officeSpacePage.stat1'), color: 'bg-red-500' },
                  { stat: '€200-400', label: t('officeSpacePage.stat2'), color: 'bg-amber-500' },
                  { stat: '15-25%', label: t('officeSpacePage.stat3'), color: 'bg-teal-600' },
                  { stat: '< 2 yrs', label: t('officeSpacePage.stat4'), color: 'bg-blue-600' },
                ].map((item, i) => (
                  <div key={i} className={`${item.color} p-6 rounded-lg text-white`}>
                    <div className="text-3xl font-bold mb-2">{item.stat}</div>
                    <p className="text-white/80 text-sm">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SMART OFFICE SENSORS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="smart-sensors">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                {t('officeSpacePage.sensorsBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t('officeSpacePage.sensorsTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('officeSpacePage.sensorsDesc')}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {/* Occupancy Sensors */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-blue-600 p-6">
                  <Users className="h-8 w-8 text-white mb-3" />
                  <h3 className="text-xl font-semibold text-white">{t('officeSpacePage.occupancySensors')}</h3>
                </div>
                <div className="p-6">
                  <p className="text-slate-600 mb-4">
                    {t('officeSpacePage.occupancySensorsDesc')}
                  </p>
                  <h4 className="font-semibold text-slate-900 mb-2 text-sm">{t('officeSpacePage.bestFor')}</h4>
                  <ul className="space-y-1">
                    {[t('officeSpacePage.openPlanOffices'), t('officeSpacePage.commonAreas'), t('officeSpacePage.cafeterias'), t('officeSpacePage.largeMeetingRooms')].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-600 text-sm">
                        <CheckCircle className="h-3 w-3 text-blue-600" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Desk Sensors */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-teal-600 p-6">
                  <Monitor className="h-8 w-8 text-white mb-3" />
                  <h3 className="text-xl font-semibold text-white">{t('officeSpacePage.deskSensors')}</h3>
                </div>
                <div className="p-6">
                  <p className="text-slate-600 mb-4">
                    {t('officeSpacePage.deskSensorsDesc')}
                  </p>
                  <h4 className="font-semibold text-slate-900 mb-2 text-sm">{t('officeSpacePage.bestFor')}</h4>
                  <ul className="space-y-1">
                    {[t('officeSpacePage.hotDeskingAreas'), t('officeSpacePage.assignedDesks'), t('officeSpacePage.focusBooths'), t('officeSpacePage.phoneRooms')].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-600 text-sm">
                        <CheckCircle className="h-3 w-3 text-teal-600" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Meeting Room Sensors */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-purple-600 p-6">
                  <Calendar className="h-8 w-8 text-white mb-3" />
                  <h3 className="text-xl font-semibold text-white">{t('officeSpacePage.meetingRoomSensors')}</h3>
                </div>
                <div className="p-6">
                  <p className="text-slate-600 mb-4">
                    {t('officeSpacePage.meetingRoomSensorsDesc')}
                  </p>
                  <h4 className="font-semibold text-slate-900 mb-2 text-sm">{t('officeSpacePage.bestFor')}</h4>
                  <ul className="space-y-1">
                    {[t('officeSpacePage.conferenceRooms'), t('officeSpacePage.huddleSpaces'), t('officeSpacePage.videoCallRooms'), t('officeSpacePage.executiveSuites')].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-600 text-sm">
                        <CheckCircle className="h-3 w-3 text-purple-600" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <img 
                src={IMAGES.sensors}
                alt="Smart office sensors - ceiling occupancy sensor, desk sensor, and meeting room panel"
                className="w-full max-w-3xl h-auto"
              />
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* INDOOR AIR QUALITY */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-50" data-testid="air-quality">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('officeSpacePage.iaqBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('officeSpacePage.iaqTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('officeSpacePage.iaqDesc')}
                </p>
                <p className="text-slate-600 mb-8 leading-relaxed">
                  {t('officeSpacePage.iaqDesc2')}
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: Activity, title: t('officeSpacePage.co2Levels'), desc: t('officeSpacePage.co2LevelsDesc') },
                    { icon: Thermometer, title: t('officeSpacePage.temperature'), desc: t('officeSpacePage.temperatureDesc') },
                    { icon: Droplets, title: t('officeSpacePage.humidity'), desc: t('officeSpacePage.humidityDesc') },
                    { icon: Wind, title: t('officeSpacePage.ventilation'), desc: t('officeSpacePage.ventilationDesc') },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="bg-white p-4 rounded-lg border border-slate-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className="h-5 w-5 text-teal-600" />
                          <h4 className="font-semibold text-slate-900 text-sm">{item.title}</h4>
                        </div>
                        <p className="text-slate-600 text-xs">{item.desc}</p>
                      </div>
                    );
                  })}
                </div>
                
                <div className="mt-6">
                  <Link 
                    to="/indoor-air-quality-monitoring"
                    className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700"
                  >
                    {t('officeSpacePage.learnMoreIAQ')}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </div>
              </div>
              
              <div className="bg-teal-600 p-8 rounded-lg text-white">
                <h4 className="font-semibold mb-6 text-xl">{t('officeSpacePage.impactTitle')}</h4>
                <div className="space-y-4">
                  {[
                    { stat: '8-11%', label: t('officeSpacePage.impactStat1') },
                    { stat: '35%', label: t('officeSpacePage.impactStat2') },
                    { stat: '23%', label: t('officeSpacePage.impactStat3') },
                  ].map((item, i) => (
                    <div key={i} className="bg-teal-700/50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-teal-100">{item.stat}</div>
                      <p className="text-teal-200 text-sm">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* OFFICE ANALYTICS PLATFORM */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="analytics-platform">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="order-2 lg:order-1">
                <img 
                  src={IMAGES.dashboard}
                  alt="Office analytics dashboard showing real-time occupancy, meeting room availability, and usage trends"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
              
              <div className="order-1 lg:order-2">
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('officeSpacePage.analyticsBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('officeSpacePage.analyticsTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('officeSpacePage.analyticsDesc')}
                </p>
                
                <h4 className="font-semibold text-slate-900 mb-4">{t('officeSpacePage.platformCapabilities')}</h4>
                <div className="space-y-3 mb-6">
                  {[
                    { icon: Monitor, title: t('officeSpacePage.realTimeDashboards'), desc: t('officeSpacePage.realTimeDashboardsDesc') },
                    { icon: MapPin, title: t('officeSpacePage.occupancyHeatmaps'), desc: t('officeSpacePage.occupancyHeatmapsDesc') },
                    { icon: Calendar, title: t('officeSpacePage.meetingRoomAnalytics'), desc: t('officeSpacePage.meetingRoomAnalyticsDesc') },
                    { icon: BarChart3, title: t('officeSpacePage.historicalTrends'), desc: t('officeSpacePage.historicalTrendsDesc') },
                    { icon: Target, title: t('officeSpacePage.spaceEfficiencyMetrics'), desc: t('officeSpacePage.spaceEfficiencyMetricsDesc') },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-start gap-3">
                        <Icon className="h-5 w-5 text-teal-600 flex-shrink-0 mt-1" />
                        <div>
                          <h5 className="font-semibold text-slate-900 text-sm">{item.title}</h5>
                          <p className="text-slate-600 text-xs">{item.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="mt-16 flex justify-center">
              <img 
                src={IMAGES.heatmap}
                alt="Office floor plan heatmap showing occupancy intensity across different zones"
                className="w-full max-w-4xl h-auto rounded-lg shadow-lg"
              />
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* HYBRID WORKPLACE MANAGEMENT */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="hybrid-workplace">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                {t('officeSpacePage.hybridBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                {t('officeSpacePage.hybridTitle')}
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed">
                {t('officeSpacePage.hybridDesc')}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: Monitor,
                  title: t('officeSpacePage.deskBookingSystems'),
                  desc: t('officeSpacePage.deskBookingSystemsDesc'),
                  color: 'bg-blue-600'
                },
                {
                  icon: Calendar,
                  title: t('officeSpacePage.meetingRoomAvailability'),
                  desc: t('officeSpacePage.meetingRoomAvailabilityDesc'),
                  color: 'bg-teal-600'
                },
                {
                  icon: LayoutGrid,
                  title: t('officeSpacePage.spacePlanning'),
                  desc: t('officeSpacePage.spacePlanningDesc'),
                  color: 'bg-purple-600'
                },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
                    <div className={`${item.color} p-6`}>
                      <Icon className="h-8 w-8 text-white mb-3" />
                      <h3 className="text-xl font-semibold text-white">{item.title}</h3>
                    </div>
                    <div className="p-6">
                      <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* BENEFITS FOR COMPANIES */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="benefits">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                {t('officeSpacePage.benefitsBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t('officeSpacePage.benefitsTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('officeSpacePage.benefitsDesc')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: DollarSign,
                  title: t('officeSpacePage.reducedCosts'),
                  desc: t('officeSpacePage.reducedCostsDesc'),
                  color: 'bg-green-600'
                },
                {
                  icon: Users,
                  title: t('officeSpacePage.betterExperience'),
                  desc: t('officeSpacePage.betterExperienceDesc'),
                  color: 'bg-blue-600'
                },
                {
                  icon: TrendingUp,
                  title: t('officeSpacePage.higherProductivity'),
                  desc: t('officeSpacePage.higherProductivityDesc'),
                  color: 'bg-purple-600'
                },
                {
                  icon: BarChart3,
                  title: t('officeSpacePage.dataDrivenPlanning'),
                  desc: t('officeSpacePage.dataDrivenPlanningDesc'),
                  color: 'bg-amber-500'
                },
                {
                  icon: Leaf,
                  title: t('officeSpacePage.sustainabilityImprovements'),
                  desc: t('officeSpacePage.sustainabilityImprovementsDesc'),
                  color: 'bg-teal-600'
                },
                {
                  icon: Target,
                  title: t('officeSpacePage.operationalVisibility'),
                  desc: t('officeSpacePage.operationalVisibilityDesc'),
                  color: 'bg-slate-700'
                },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="bg-slate-50 border border-slate-200 rounded-lg p-6">
                    <div className={`w-12 h-12 ${item.color} rounded-lg flex items-center justify-center mb-4`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-slate-600 text-sm">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* USE CASES */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-50" data-testid="use-cases">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                {t('officeSpacePage.useCasesBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t('officeSpacePage.useCasesTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('officeSpacePage.useCasesDesc')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: Building2,
                  title: t('officeSpacePage.corporateHQ'),
                  desc: t('officeSpacePage.corporateHQDesc'),
                },
                {
                  icon: Monitor,
                  title: t('officeSpacePage.techCompanies'),
                  desc: t('officeSpacePage.techCompaniesDesc'),
                },
                {
                  icon: Building2,
                  title: t('officeSpacePage.governmentBuildings'),
                  desc: t('officeSpacePage.governmentBuildingsDesc'),
                },
                {
                  icon: Users,
                  title: t('officeSpacePage.universities'),
                  desc: t('officeSpacePage.universitiesDesc'),
                },
                {
                  icon: LayoutGrid,
                  title: t('officeSpacePage.coworkingSpaces'),
                  desc: t('officeSpacePage.coworkingSpacesDesc'),
                },
                {
                  icon: Building2,
                  title: t('officeSpacePage.commercialRealEstate'),
                  desc: t('officeSpacePage.commercialRealEstateDesc'),
                },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="bg-white border border-slate-200 rounded-lg p-6">
                    <Icon className="h-8 w-8 text-teal-600 mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-slate-600 text-sm">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* IOT CONNECTIVITY */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="iot-connectivity">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('officeSpacePage.iotConnectivityBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('officeSpacePage.iotConnectivityTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('officeSpacePage.iotConnectivityDesc')}
                </p>
                
                <h4 className="font-semibold text-slate-900 mb-4">{t('officeSpacePage.connectivityOptions')}</h4>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {[
                    { icon: Radio, name: t('officeSpacePage.lorawanName'), desc: t('officeSpacePage.lorawanDesc') },
                    { icon: Wifi, name: t('officeSpacePage.nbiotName'), desc: t('officeSpacePage.nbiotDesc') },
                    { icon: Wifi, name: t('officeSpacePage.ltemName'), desc: t('officeSpacePage.ltemDesc') },
                    { icon: Wifi, name: t('officeSpacePage.wifiName'), desc: t('officeSpacePage.wifiDesc') },
                  ].map((tech, i) => {
                    const Icon = tech.icon;
                    return (
                      <div key={i} className="bg-slate-50 p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className="h-5 w-5 text-teal-600" />
                          <h5 className="font-semibold text-slate-900 text-sm">{tech.name}</h5>
                        </div>
                        <p className="text-slate-600 text-xs">{tech.desc}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="bg-slate-900 p-8 rounded-lg">
                <h4 className="text-white font-semibold mb-6 text-lg">{t('officeSpacePage.keyAdvantages')}</h4>
                <div className="space-y-4">
                  {[
                    { icon: Battery, title: t('officeSpacePage.longBatteryLife'), desc: t('officeSpacePage.longBatteryLifeDesc') },
                    { icon: Settings, title: t('officeSpacePage.lowMaintenance'), desc: t('officeSpacePage.lowMaintenanceDesc') },
                    { icon: Shield, title: t('officeSpacePage.secureCommunication'), desc: t('officeSpacePage.secureCommunicationDesc') },
                    { icon: LayoutGrid, title: t('officeSpacePage.scalableDeployment'), desc: t('officeSpacePage.scalableDeploymentDesc') },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h5 className="text-white font-semibold text-sm">{item.title}</h5>
                          <p className="text-slate-400 text-xs">{item.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="mt-6">
                  <Link 
                    to="/connectivity"
                    className="inline-flex items-center text-teal-400 font-semibold hover:text-teal-300"
                  >
                    {t('officeSpacePage.learnMoreConnectivity')}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* WHY 0G BALTICS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="why-0g-baltics">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                  {t('officeSpacePage.whyBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                  {t('officeSpacePage.whyTitle')}
                </h2>
                <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                  {t('officeSpacePage.whyDesc')}
                </p>
                
                <div className="space-y-4">
                  {[
                    { title: t('officeSpacePage.service1'), desc: t('officeSpacePage.service1Desc') },
                    { title: t('officeSpacePage.service2'), desc: t('officeSpacePage.service2Desc') },
                    { title: t('officeSpacePage.service3'), desc: t('officeSpacePage.service3Desc') },
                    { title: t('officeSpacePage.service4'), desc: t('officeSpacePage.service4Desc') },
                    { title: t('officeSpacePage.service5'), desc: t('officeSpacePage.service5Desc') },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <CheckCircle className="h-5 w-5 text-teal-400 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-semibold text-white">{item.title}</h4>
                        <p className="text-slate-400 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-slate-800 p-8 rounded-lg border border-slate-700">
                  <h4 className="text-white font-semibold mb-6 text-lg">{t('officeSpacePage.relatedSolutions')}</h4>
                  <div className="space-y-4">
                    {[
                      { to: '/smart-city-iot-infrastructure', label: t('officeSpacePage.relatedSmartCity'), desc: t('officeSpacePage.relatedSmartCityDesc') },
                      { to: '/smart-lockers', label: t('officeSpacePage.relatedSmartLockers'), desc: t('officeSpacePage.relatedSmartLockersDesc') },
                      { to: '/asset-tracking', label: t('officeSpacePage.relatedAssetTracking'), desc: t('officeSpacePage.relatedAssetTrackingDesc') },
                      { to: '/connectivity', label: t('officeSpacePage.relatedConnectivity'), desc: t('officeSpacePage.relatedConnectivityDesc') },
                    ].map((link, i) => (
                      <Link key={i} to={link.to} className="block bg-slate-700 p-4 rounded-lg hover:bg-slate-600 transition-colors group">
                        <div className="flex items-center justify-between">
                          <div>
                            <h5 className="text-white font-medium">{link.label}</h5>
                            <p className="text-slate-400 text-sm">{link.desc}</p>
                          </div>
                          <ArrowRight className="h-5 w-5 text-teal-400 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* CTA SECTION */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-teal-600" data-testid="cta-section">
          <div className="container-0g text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('officeSpacePage.ctaTitle')}
            </h2>
            <p className="text-xl text-teal-100 mb-8 max-w-3xl mx-auto">
              {t('officeSpacePage.ctaDesc')}
            </p>
            <Button 
              size="lg"
              className="bg-white text-teal-700 hover:bg-teal-50 font-semibold"
              onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="cta-button"
            >
              {t('officeSpacePage.ctaButton')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* ========================================= */}
        {/* CONTACT FORM */}
        {/* ========================================= */}
        <div id="contact-form">
          <ContactForm />
        </div>

      </main>

      <Footer />
    </>
  );
};

export default OfficeSpaceManagementPage;
